import { hash } from 'ohash';

const prefetchPending = new Set();
const prefetchDone = new Set();

export function baseRouteName(name) {
    if (typeof name !== 'string') return null;
    // @nuxtjs/i18n: "products-slug___en" → "products-slug"
    return name.replace(/___[a-z]{2}(-[a-zA-Z]+)?$/, '');
}

export function resolveGqlPrefetchFromRoute(route) {
    const name = baseRouteName(route?.name);
    if (!name) return null;

    const slug = route.params?.slug;
    const decodedSlug = typeof slug === 'string' ? decodeURIComponent(slug) : Array.isArray(slug) ? decodeURIComponent(slug[0]) : null;

    switch (name) {
        case 'index':
            return { operation: 'home' };
        case 'distributor':
            return { operation: 'distributorhub' };
        case 'set-up-maintenance':
            return { operation: 'maintenance' };
        case 'products-type-slug':
            return decodedSlug ? { operation: 'type', variables: { slug: decodedSlug } } : null;
        case 'products-slug':
            return decodedSlug ? { operation: 'product', variables: { slug: decodedSlug } } : null;
        case 'community-slug':
            return decodedSlug ? { operation: 'article', variables: { slug: decodedSlug } } : null;
        case 'slug':
            return decodedSlug ? { operation: 'page', variables: { slug: decodedSlug } } : null;
        default:
            return null;
    }
}

function normalizeInternalPath(href) {
    try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return null;
        return `${url.pathname}${url.search}${url.hash}`;
    } catch {
        return null;
    }
}

export function useRoutePrefetch() {
    const router = useRouter();

    const prefetchPath = async (path) => {
        if (!import.meta.client || !path) return;

        let pathname = path;
        try {
            const url = new URL(path, window.location.origin);
            if (url.origin !== window.location.origin) return;
            pathname = `${url.pathname}${url.search}`;
        } catch {
            return;
        }

        try {
            preloadRouteComponents(pathname);
        } catch {
            // route may not exist yet
        }

        let resolvedRoute;
        try {
            resolvedRoute = router.resolve(pathname);
        } catch {
            return;
        }

        const resolved = resolveGqlPrefetchFromRoute(resolvedRoute);
        if (!resolved) return;

        const key = `gql:data:${hash({
            operation: resolved.operation,
            variables: resolved.variables
        })}`;

        if (prefetchDone.has(key) || prefetchPending.has(key)) return;

        const nuxtApp = useNuxtApp();
        if (nuxtApp.payload.data[key] != null) {
            prefetchDone.add(key);
            return;
        }

        prefetchPending.add(key);

        try {
            const result = await useGql()(resolved.operation, resolved.variables);
            nuxtApp.payload.data[key] = result;
            prefetchDone.add(key);
        } catch {
            // Prefetch failures should not block navigation
        } finally {
            prefetchPending.delete(key);
        }
    };

    const bindDocumentPrefetch = () => {
        if (!import.meta.client) return () => {};

        const globalStore = useGlobalStore();
        const prefetchedHrefs = new Set();

        const maybePrefetch = (event) => {
            if (!globalStore.appLoadingComplete) return;

            const anchor = event.target?.closest?.('a[href]');
            if (!anchor) return;

            const path = normalizeInternalPath(anchor.getAttribute('href'));
            if (!path || prefetchedHrefs.has(path)) return;

            prefetchedHrefs.add(path);
            prefetchPath(path);
        };

        document.addEventListener('pointerover', maybePrefetch, true);
        document.addEventListener('focusin', maybePrefetch, true);

        return () => {
            document.removeEventListener('pointerover', maybePrefetch, true);
            document.removeEventListener('focusin', maybePrefetch, true);
        };
    };

    return { prefetchPath, bindDocumentPrefetch };
}
